/**
 * DataShield Governance Portal - Global State Store
 * 
 * Uses Zustand for lightweight, testable state management.
 * Each domain has its own slice for separation of concerns.
 * 
 * @module store/index
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User, UserPreferences, Alert, AIDecision } from '@/types';

// =============================================================================
// SLICE TYPES
// =============================================================================

interface UserSlice {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => void;
  logout: () => void;
}

interface UISlice {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  activePanel: 'none' | 'detail' | 'ai' | 'both';
  panelEntityId: string | null;
  panelEntityType: string | null;
  theme: 'light' | 'dark' | 'system' | 'high-contrast';
  homeScreen: 'manual' | 'ai-first';
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;
  openPanel: (type: 'detail' | 'ai' | 'both', entityId?: string, entityType?: string) => void;
  closePanel: () => void;
  setTheme: (theme: UISlice['theme']) => void;
  setHomeScreen: (screen: UISlice['homeScreen']) => void;
}

interface AlertSlice {
  alerts: Alert[];
  unreadCount: number;
  isLoading: boolean;
  setAlerts: (alerts: Alert[]) => void;
  addAlert: (alert: Alert) => void;
  acknowledgeAlert: (id: string) => void;
  dismissAlert: (id: string) => void;
  clearAll: () => void;
}

interface AISlice {
  pendingDecisions: AIDecision[];
  pendingCount: number;
  isLoading: boolean;
  chatHistory: ChatMessage[];
  aiContext: AIContext | null;
  setPendingDecisions: (decisions: AIDecision[]) => void;
  approveDecision: (id: string) => void;
  rejectDecision: (id: string, reason?: string) => void;
  addChatMessage: (message: ChatMessage) => void;
  clearChatHistory: () => void;
  setAIContext: (context: AIContext | null) => void;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  actions?: ChatAction[];
}

interface ChatAction {
  id: string;
  label: string;
  action: string;
  params?: Record<string, unknown>;
}

interface AIContext {
  screen: string;
  entityType?: string;
  entityId?: string;
  filters?: Record<string, unknown>;
  recentActions?: string[];
}

interface ToastSlice {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// =============================================================================
// COMBINED STORE TYPE
// =============================================================================

type StoreState = UserSlice & UISlice & AlertSlice & AISlice & ToastSlice;

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const useStore = create<StoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // =====================================================================
        // USER SLICE
        // =====================================================================
        user: null,
        isAuthenticated: false,
        isLoading: false,
        
        setUser: (user) => set({ 
          user, 
          isAuthenticated: !!user,
          homeScreen: user?.preferences?.homeScreen || 'manual',
          theme: user?.preferences?.theme || 'light',
        }),
        
        updatePreferences: (preferences) => set((state) => ({
          user: state.user ? {
            ...state.user,
            preferences: { ...state.user.preferences, ...preferences },
          } : null,
          ...(preferences.homeScreen && { homeScreen: preferences.homeScreen }),
          ...(preferences.theme && { theme: preferences.theme }),
        })),
        
        logout: () => set({ 
          user: null, 
          isAuthenticated: false,
          chatHistory: [],
          aiContext: null,
        }),

        // =====================================================================
        // UI SLICE
        // =====================================================================
        sidebarOpen: true,
        sidebarCollapsed: false,
        activePanel: 'none',
        panelEntityId: null,
        panelEntityType: null,
        theme: 'light',
        homeScreen: 'manual',
        
        toggleSidebar: () => set((state) => ({ 
          sidebarOpen: !state.sidebarOpen 
        })),
        
        toggleSidebarCollapse: () => set((state) => ({ 
          sidebarCollapsed: !state.sidebarCollapsed 
        })),
        
        openPanel: (type, entityId, entityType) => set({ 
          activePanel: type,
          panelEntityId: entityId || null,
          panelEntityType: entityType || null,
        }),
        
        closePanel: () => set({ 
          activePanel: 'none',
          panelEntityId: null,
          panelEntityType: null,
        }),
        
        setTheme: (theme) => {
          // Apply theme to document
          document.documentElement.setAttribute('data-theme', theme === 'system' 
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : theme
          );
          set({ theme });
        },
        
        setHomeScreen: (homeScreen) => set({ homeScreen }),

        // =====================================================================
        // ALERT SLICE
        // =====================================================================
        alerts: [],
        unreadCount: 0,
        
        setAlerts: (alerts) => set({ 
          alerts,
          unreadCount: alerts.filter((a) => a.status === 'active').length,
        }),
        
        addAlert: (alert) => set((state) => {
          const alerts = [alert, ...state.alerts];
          return { 
            alerts,
            unreadCount: alerts.filter((a) => a.status === 'active').length,
          };
        }),
        
        acknowledgeAlert: (id) => set((state) => ({
          alerts: state.alerts.map((a) => 
            a.id === id ? { ...a, status: 'acknowledged' as const, acknowledgedAt: new Date().toISOString() } : a
          ),
          unreadCount: Math.max(0, state.unreadCount - 1),
        })),
        
        dismissAlert: (id) => set((state) => ({
          alerts: state.alerts.filter((a) => a.id !== id),
          unreadCount: state.alerts.find((a) => a.id === id)?.status === 'active' 
            ? Math.max(0, state.unreadCount - 1) 
            : state.unreadCount,
        })),
        
        clearAll: () => set({ alerts: [], unreadCount: 0 }),

        // =====================================================================
        // AI SLICE
        // =====================================================================
        pendingDecisions: [],
        pendingCount: 0,
        chatHistory: [],
        aiContext: null,
        
        setPendingDecisions: (decisions) => set({ 
          pendingDecisions: decisions,
          pendingCount: decisions.length,
        }),
        
        approveDecision: (id) => set((state) => ({
          pendingDecisions: state.pendingDecisions.filter((d) => d.id !== id),
          pendingCount: Math.max(0, state.pendingCount - 1),
        })),
        
        rejectDecision: (id, _reason) => set((state) => ({
          pendingDecisions: state.pendingDecisions.filter((d) => d.id !== id),
          pendingCount: Math.max(0, state.pendingCount - 1),
        })),
        
        addChatMessage: (message) => set((state) => ({
          chatHistory: [...state.chatHistory, message],
        })),
        
        clearChatHistory: () => set({ chatHistory: [] }),
        
        setAIContext: (context) => set({ aiContext: context }),

        // =====================================================================
        // TOAST SLICE
        // =====================================================================
        toasts: [],
        
        addToast: (toast) => set((state) => ({
          toasts: [...state.toasts, { ...toast, id: `toast-${Date.now()}` }],
        })),
        
        removeToast: (id) => set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),
      }),
      {
        name: 'datashield-store',
        partialize: (state) => ({
          theme: state.theme,
          homeScreen: state.homeScreen,
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    )
  )
);

// =============================================================================
// SELECTORS (for performance optimization)
// =============================================================================

export const selectUser = (state: StoreState) => state.user;
export const selectIsAuthenticated = (state: StoreState) => state.isAuthenticated;
export const selectTheme = (state: StoreState) => state.theme;
export const selectHomeScreen = (state: StoreState) => state.homeScreen;
export const selectSidebarOpen = (state: StoreState) => state.sidebarOpen;
export const selectSidebarCollapsed = (state: StoreState) => state.sidebarCollapsed;
export const selectActivePanel = (state: StoreState) => state.activePanel;
export const selectAlerts = (state: StoreState) => state.alerts;
export const selectUnreadAlertCount = (state: StoreState) => state.unreadCount;
export const selectPendingDecisions = (state: StoreState) => state.pendingDecisions;
export const selectPendingCount = (state: StoreState) => state.pendingCount;
export const selectChatHistory = (state: StoreState) => state.chatHistory;
export const selectToasts = (state: StoreState) => state.toasts;

// =============================================================================
// HOOKS (typed convenience hooks)
// =============================================================================

export const useUser = () => useStore((state) => state.user);
export const useIsAuthenticated = () => useStore((state) => state.isAuthenticated);
export const useTheme = () => useStore((state) => state.theme);
export const useHomeScreen = () => useStore((state) => state.homeScreen);
export const useSidebar = () => useStore((state) => ({
  open: state.sidebarOpen,
  collapsed: state.sidebarCollapsed,
  toggle: state.toggleSidebar,
  toggleCollapse: state.toggleSidebarCollapse,
}));
export const usePanel = () => useStore((state) => ({
  active: state.activePanel,
  entityId: state.panelEntityId,
  entityType: state.panelEntityType,
  open: state.openPanel,
  close: state.closePanel,
}));
export const useAlerts = () => useStore((state) => ({
  alerts: state.alerts,
  unreadCount: state.unreadCount,
  acknowledge: state.acknowledgeAlert,
  dismiss: state.dismissAlert,
}));
export const useAI = () => useStore((state) => ({
  pendingDecisions: state.pendingDecisions,
  pendingCount: state.pendingCount,
  chatHistory: state.chatHistory,
  context: state.aiContext,
  approve: state.approveDecision,
  reject: state.rejectDecision,
  addMessage: state.addChatMessage,
  clearChat: state.clearChatHistory,
  setContext: state.setAIContext,
}));
export const useToasts = () => useStore((state) => ({
  toasts: state.toasts,
  add: state.addToast,
  remove: state.removeToast,
}));
